'use client'

import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import type { Session } from 'next-auth'
import { getSession, signOut, SessionProvider, useSession } from 'next-auth/react'
import {
  IDLE_TIMEOUT_SECONDS,
  SESSION_UPDATE_AGE_SECONDS,
} from '@/lib/auth-constants'

/**
 * NextAuth Session Provider
 *
 * Authentication is handled at the edge via proxy (see proxy.ts).
 * This provider only makes session data available to client components.
 *
 * Security model:
 * - Route protection: proxy.ts (edge-level, no flash)
 * - API 401 handling: lib/api/client.ts (silent redirect)
 * - Session data access + activity tracking: this provider (for UI state)
 */

interface AuthProviderProps {
  children: ReactNode
  session?: Session | null
}

const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_SECONDS * 1000
const ACTIVITY_REFRESH_THROTTLE_MS = SESSION_UPDATE_AGE_SECONDS * 1000

const isSafeRelativeUrl = (value: string | null): boolean => {
  if (!value) return false
  if (!value.startsWith('/')) return false
  return !value.startsWith('//')
}

const buildLoginCallbackUrl = (errorType: string) => {
  if (typeof window === 'undefined') {
    return '/auth/login'
  }

  const returnTo = `${window.location.pathname}${window.location.search}`
  const safeReturnTo = isSafeRelativeUrl(returnTo) ? returnTo : null
  const base = `/auth/login?error=${encodeURIComponent(errorType)}`
  return safeReturnTo
    ? `${base}&returnTo=${encodeURIComponent(safeReturnTo)}`
    : base
}

function AuthSessionManager() {
  const { data: session, status } = useSession()
  const idleTimerRef = useRef<number | null>(null)
  const lastRefreshRef = useRef(0)
  const signOutStartedRef = useRef(false)

  const clearIdleTimer = () => {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }

  const scheduleIdleTimer = () => {
    clearIdleTimer()
    idleTimerRef.current = window.setTimeout(() => {
      if (signOutStartedRef.current) return
      signOutStartedRef.current = true
      void signOut({
        callbackUrl: buildLoginCallbackUrl('SessionExpired'),
      })
    }, IDLE_TIMEOUT_MS)
  }

  const handleActivity = () => {
    if (status !== 'authenticated') return
    scheduleIdleTimer()

    const now = Date.now()
    if (now - lastRefreshRef.current < ACTIVITY_REFRESH_THROTTLE_MS) {
      return
    }

    lastRefreshRef.current = now
    void getSession()
  }

  useEffect(() => {
    if (status !== 'authenticated') {
      clearIdleTimer()
      signOutStartedRef.current = false
      return
    }

    scheduleIdleTimer()

    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'focus',
    ]

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleActivity()
      }
    }

    events.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    )
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearIdleTimer()
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      )
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [status])

  useEffect(() => {
    if (!session?.error || signOutStartedRef.current) return
    const errorType =
      session.error === 'SessionExpired'
        ? 'SessionExpired'
        : 'SessionRequired'
    signOutStartedRef.current = true
    void signOut({
      callbackUrl: buildLoginCallbackUrl(errorType),
    })
  }, [session?.error])

  return null
}

export function AuthProvider({ children, session }: AuthProviderProps) {
  return (
    <SessionProvider
      session={session}
      // Refetch only on explicit user activity via AuthSessionManager.
      refetchInterval={0}
      refetchOnWindowFocus={false}
      refetchWhenOffline={false}
    >
      <AuthSessionManager />
      {children}
    </SessionProvider>
  )
}

export default AuthProvider
